// Base fetch wrapper — attaches common headers and handles errors uniformly.

export class ApiError extends Error {
  constructor(
    public status: number,
    message: string
  ) {
    super(message);
    this.name = "ApiError";
  }
}

async function apiFetch<T>(path: string, init?: RequestInit): Promise<T> {
  const res = await fetch(path, {
    ...init,
    headers: {
      "Content-Type": "application/json",
      ...init?.headers,
    },
  });

  if (!res.ok) {
    throw new ApiError(res.status, `API error ${res.status}: ${res.statusText}`);
  }

  if (res.status === 204) return undefined as T;

  return res.json() as Promise<T>;
}

// --- Types ---

export interface AppRoute {
  slug: string;
  label: string;
  icon: string; // emoji
  url: string;
  description?: string;
}

export interface MemoryEntry {
  id: string;
  content: string;
  tags: string[];
  created_at: string; // ISO 8601
  source?: string;
}

interface MemoryApiRecord {
  id?: string;
  content?: string;
  text?: string;
  raw_text?: string;
  tags?: unknown;
  created_at?: string;
  source?: string;
  entry?: {
    id?: string;
    content?: string;
    text?: string;
    raw_text?: string;
    tags?: unknown;
    created_at?: string;
    source?: string;
  };
}

interface MemoryApiResponse {
  entries?: MemoryApiRecord[];
  results?: MemoryApiRecord[];
}

function normalizeTags(tags: unknown): string[] {
  return Array.isArray(tags) ? tags.filter((tag): tag is string => typeof tag === "string") : [];
}

function normalizeMemoryEntry(entry: MemoryApiRecord, index: number): MemoryEntry | null {
  const nested = entry.entry;
  const content =
    entry.content ??
    entry.text ??
    entry.raw_text ??
    nested?.content ??
    nested?.text ??
    nested?.raw_text;
  const createdAt = entry.created_at ?? nested?.created_at;

  if (!content || !createdAt) {
    return null;
  }

  return {
    id: entry.id ?? nested?.id ?? `memory-${index}-${createdAt}`,
    content,
    tags: normalizeTags(entry.tags ?? nested?.tags),
    created_at: createdAt,
    source: entry.source ?? nested?.source,
  };
}

function normalizeMemoryResponse(data: unknown): MemoryEntry[] {
  const records = Array.isArray(data)
    ? data
    : typeof data === "object" && data !== null
      ? ((data as MemoryApiResponse).entries ?? (data as MemoryApiResponse).results ?? [])
      : [];

  return records
    .map((entry, index) => normalizeMemoryEntry(entry as MemoryApiRecord, index))
    .filter((entry): entry is MemoryEntry => entry !== null);
}

export interface ActiveTask {
  id: string;
  label: string;
  status: "pending" | "running" | "complete" | "failed";
  started_at: string;
  updated_at: string;
  output?: string;
}

// --- API functions ---

/** Fetch app routes for the Launcher from the router Worker's KV table. */
export async function fetchRoutes(): Promise<AppRoute[]> {
  return apiFetch<AppRoute[]>("/api/routes");
}

/** Fetch recent memory entries from open-brain. Optionally filter by tier, limit count, and cursor. */
export async function fetchRecentMemory(
  days = 7,
  tier?: "T1" | "T2",
  limit = 100,
  before?: string
): Promise<MemoryEntry[]> {
  const params = new URLSearchParams({ days: String(days), limit: String(limit) });
  if (tier) params.set("tier", tier);
  if (before) params.set("before", before);
  const data = await apiFetch<unknown>(`/api/memory/recent?${params}`);
  return normalizeMemoryResponse(data);
}

/** Delete a memory entry by ID. */
export async function deleteMemory(id: string, tier: "T1" | "T2"): Promise<void> {
  return apiFetch<void>(`/api/memory/${encodeURIComponent(id)}?tier=${tier}`, {
    method: "DELETE",
  });
}

/**
 * Update a memory entry by deleting and re-capturing with the new content.
 * open-brain has no native update endpoint, so this is delete + recapture.
 * Returns the new entry so the cache can be patched in-place.
 */
export async function updateMemory(
  id: string,
  text: string,
  tags: string[],
  tier: "T1" | "T2"
): Promise<MemoryEntry> {
  await deleteMemory(id, tier);
  const raw = await apiFetch<MemoryApiRecord>("/api/memory/capture", {
    method: "POST",
    body: JSON.stringify({ text, tier, tags }),
  });
  const entry = normalizeMemoryEntry(raw, 0);
  if (!entry) throw new Error("Capture succeeded but returned no entry");
  return entry;
}

/** Fetch in-flight background tasks from hal-gateway. */
export async function fetchActiveTasks(): Promise<ActiveTask[]> {
  return apiFetch<ActiveTask[]>("/api/tasks");
}

/** Send a quick capture message to hal-mcp. */
export async function quickCapture(content: string): Promise<void> {
  return apiFetch<void>("/api/hal/capture", {
    method: "POST",
    body: JSON.stringify({ content }),
  });
}

// --- Overview page: vault projection + merged calendar (read-only) ---

export type Domain = "cetec" | "mkbd" | "personal";

export interface ProjectRecord {
  slug: string;
  name: string;
  path: string;
  domain: Domain;
  category: string[];
  status: string;
  logArea?: string;
  updated?: string; // YYYY-MM-DD
  started?: string;
  people: string[];
  /** null for personal notes — they have no checkboxes by design */
  open: number | null;
  done: number | null;
  waiting: number;
  nextDate?: string; // earliest 📅 date >= today in this note
  last?: string; // personal: first line of "Where it stands"
  next?: string; // personal: first "## Next" bullet
  note?: string; // work: first line of "Where it stands"
}

export interface TaskRecord {
  /** Stable: t_ + sha256(path + "\n" + raw)[:12]. Survives line moves; changes when the text changes. */
  id: string;
  project: string; // ProjectRecord.slug
  path: string; // vault-relative, e.g. "Projects/Foo.md"
  text: string;
  raw: string; // the line exactly as in the file (trailing whitespace removed)
  hash: string; // sha256(raw)[:16]
  waiting: boolean;
  waitingOn?: string;
  due?: string; // YYYY-MM-DD — a real deadline 📅
  remindOn?: string; // YYYY-MM-DD — a #remind 📅 date, never a deadline
  line: number; // display only
  /** P13: the phase id (p_…) when the task sits under a "### Phase" + "Plan:" heading. */
  phase?: string;
}

// --- Phases (P13): "### Name" + "Plan: start → end" inside ## Tasks ---

export type PhaseProblemKind =
  | "plan-unreadable"
  | "plan-reversed"
  | "plan-orphan"
  | "duplicate-name";

export interface PhaseMilestone {
  taskId: string;
  text: string; // plain text
  due: string; // the deadline 📅
  open: boolean;
}

export interface PhaseRecord {
  /** Stable: p_ + sha256(path + "\n" + heading)[:12]. Survives a date change; changes on a rename. */
  id: string;
  project: string; // ProjectRecord.slug
  path: string; // vault-relative, NFC
  name: string; // heading text without "### "
  heading: string; // raw heading line
  planRaw: string; // raw Plan: line
  hash: string; // sha256(heading + "\n" + planRaw)[:16] — the text-match check for a drag
  /** YYYY-MM-DD. null only when the Plan line is unreadable (problem: plan-unreadable). */
  start: string | null;
  end: string | null; // inclusive
  line: number; // heading line, display only
  order: number; // position in the note, 0-based
  open: number;
  done: number;
  state: "open" | "done" | "empty";
  milestones: PhaseMilestone[];
  /** The latest milestone 📅 differs from the plan end. Shown, never fixed. */
  mismatch?: { milestone: string; planEnd: string; taskId: string };
  /** Set when the reader found a problem with this phase. It cannot be dragged. */
  problem?: PhaseProblemKind;
}

export interface PhaseProblem {
  path: string;
  line: number;
  kind: PhaseProblemKind;
  text: string; // the line as in the file
}

export interface UnphasedMilestone {
  project: string;
  taskId: string;
  text: string;
  due: string;
  open: boolean;
}

export interface UnsortedRecord {
  slug: string;
  name: string;
  category: string[];
}

export interface Projection {
  generatedAt: string;
  projects: ProjectRecord[];
  tasks: TaskRecord[];
  unsorted: UnsortedRecord[];
  /** P13. Optional: a vault-reader older than P13 does not send them. */
  phases?: PhaseRecord[];
  phaseProblems?: PhaseProblem[];
  unphasedMilestones?: UnphasedMilestone[];
}

export interface CalendarEvent {
  id: string;
  source: "google" | "local";
  accountEmail: string | null;
  calendarId: string | null;
  calendarName: string | null;
  color: string | null;
  title: string;
  start: string; // ISO datetime, or YYYY-MM-DD when allDay
  end: string;
  allDay: boolean;
  location: string | null;
  url: string | null;
}

interface EventsResponse {
  from: string;
  to: string;
  events: CalendarEvent[];
}

/** Fetch the vault projection served by the vault-reader on bee. */
export async function fetchProjection(): Promise<Projection> {
  return apiFetch<Projection>("/api/vault/projection");
}

/** Fetch merged calendar events (all connected accounts) for an inclusive day range. */
export async function fetchEvents(from: string, to: string): Promise<CalendarEvent[]> {
  const params = new URLSearchParams({ from, to });
  const data = await apiFetch<EventsResponse>(`/api/cal/events?${params}`);
  return data.events ?? [];
}

export interface TimeOffBlock {
  id: string;
  start: string; // YYYY-MM-DD, first day
  end: string; // YYYY-MM-DD, last day (inclusive)
  hours: number | null; // hours Gusto recorded; null when unknown
  label: string;
}

interface TimeOffResponse {
  from: string;
  to: string;
  calendarId: string;
  accountEmail: string;
  blocks: TimeOffBlock[];
}

/** Fetch Gusto time-off blocks (via calendo) for an inclusive day range. */
export async function fetchTimeOff(from: string, to: string): Promise<TimeOffBlock[]> {
  const params = new URLSearchParams({ from, to });
  const data = await apiFetch<TimeOffResponse>(`/api/cal/timeoff?${params}`);
  return data.blocks ?? [];
}

// --- Triage mode (P12): candidates + Hal's labels + batch state; one write (send a batch) ---

export type TriageLabelKind = "likely-done" | "stale" | "live" | "waiting";
export type TriageAction = "done" | "drop" | "keep" | "snooze" | "reschedule" | "note";

export interface TriageEvidence {
  kind: "pr" | "issue" | "commit" | "file" | "email" | "calendar" | "note" | "page";
  ref: string;
  url?: string;
  state?: string;
  title?: string;
}

export interface TriageLabel {
  taskId: string;
  path: string;
  hash: string;
  label: TriageLabelKind;
  confidence: "strong" | "weak";
  why: string;
  evidence: TriageEvidence[];
  suggest?: "done" | "drop" | "keep" | "snooze";
}

export interface TriageChild {
  id: string;
  raw: string;
  hash: string;
  text: string;
}

export interface TriageCandidate {
  id: string;
  path: string;
  project: string;
  projectName: string;
  domain: Domain;
  noteStatus: string; // active | complete | superseded
  section: string | null; // "## Tasks", "## Open items", …
  heading: string | null;
  inTasks: boolean;
  line: number;
  raw: string;
  hash: string;
  text: string;
  due?: string;
  dueCount: number;
  remindOn?: string;
  waiting: boolean;
  nested: boolean;
  parent?: { id: string; text: string; open: boolean };
  children: TriageChild[];
  label: TriageLabel | null;
  /** First known date (YYYY-MM-DD) from bee's store, or null. Derived; task lines carry no ➕ field. */
  added?: string | null;
  /** history = earliest Hal session that wrote the line; bee = first seen by vault-reader; unknown */
  addedSource?: "history" | "bee" | "unknown";
}

export interface TriageBatchState {
  id: string;
  status: "pending" | "applied" | "rejected";
  createdAt: string | null;
  appliedAt: string | null;
  decisions: number;
  skipped: number | null;
  rejectedWhy: string | null;
}

export interface TriageRecent {
  action: TriageAction | null;
  outcome: "applied" | "no-edit" | "already-closed" | "already-applied" | "skipped";
  why: string | null;
  at: string | null;
  batch: string;
}

export interface TriageView {
  generatedAt: string;
  labelsAt: string | null;
  candidates: TriageCandidate[];
  batches: TriageBatchState[];
  pending: string[];
  recent: Record<string, TriageRecent>;
  errors: Array<{ path: string; message: string }>;
}

export interface TriageSummary {
  generatedAt: string;
  labelsAt: string | null;
  pending: string[];
  /** P13: phase id → the range in an unapplied batch. Optional: older readers do not send it. */
  pendingPlans?: Record<string, { start: string; end: string; batch: string }>;
  counts: { candidates: number; labeled: number; pendingBatches: number };
}

export interface TriageDecisionInput {
  taskId: string;
  path: string;
  raw: string;
  hash: string;
  action: TriageAction;
  date?: string;
  fromDue?: string;
  reason?: string;
  note?: string;
  children?: Array<{ raw: string; hash: string }>;
  label?: { label: string; confidence?: string; generatedAt?: string };
  stagedAt: string;
}

/** P13: one Gantt drag. taskId holds the phase id, so every reader keyed by taskId works unchanged. */
export interface PlanDecisionInput {
  taskId: string; // p_… phase id
  path: string; // Projects/….md
  heading: string; // "### Discover"
  raw: string; // "Plan: 2026-11-02 → 2026-11-16"
  hash: string; // sha256(heading + "\n" + raw)[:16]
  action: "replan";
  from: { start: string; end: string }; // what the bar showed
  to: { start: string; end: string }; // where Shaun dropped it
  note?: string; // stored, never executed
  stagedAt: string;
}

export interface TriageBatchInput {
  schema: 1;
  clientBatchId: string;
  seen: { projectionAt: string; labelsAt?: string };
  decisions: Array<TriageDecisionInput | PlanDecisionInput>;
}

export interface TriageSendResult {
  id: string;
  duplicate: boolean;
  decisions: number;
  obsidianRunning: boolean;
}

/** Candidates, labels and batch state from the vault-reader on bee. */
export async function fetchTriage(): Promise<TriageView> {
  return apiFetch<TriageView>("/api/vault/triage");
}

/** Pending task ids and counts, for badges on the overview page. */
export async function fetchTriageSummary(): Promise<TriageSummary> {
  return apiFetch<TriageSummary>("/api/vault/triage?summary=1");
}

/** Send a batch. bee saves it as a new file; Hal on `work` applies it. Errors carry bee's message. */
export async function sendTriageBatch(batch: TriageBatchInput): Promise<TriageSendResult> {
  const res = await fetch("/api/vault/triage/batches", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(batch),
  });
  const data = (await res.json().catch(() => ({}))) as Partial<TriageSendResult> & {
    error?: string;
    details?: string[];
  };
  if (!res.ok) {
    const detail = data.details?.length ? ` — ${data.details.slice(0, 3).join("; ")}` : "";
    throw new ApiError(res.status, `${data.error ?? `send failed (${res.status})`}${detail}`);
  }
  return data as TriageSendResult;
}
